import { Injectable } from '@angular/core';

const STORAGE_KEY = 'temporary-study-items';
const ALL_ADDED_KEY = 'temporary-study-all-added';

@Injectable({
  providedIn: 'root',
})
export class TemporaryStudyItemsService {
  private items: string[] = [];

  constructor() {
    this.loadItems();
  }

  private loadItems(): void {
    const storedItems = localStorage.getItem(STORAGE_KEY);
    this.items = storedItems ? JSON.parse(storedItems) : [];
  }

  private saveItems(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
  }

  getItems(): string[] {
    return [...this.items];
  }

  addItem(itemId: string): void {
    if (!this.items.includes(itemId)) {
      this.items.push(itemId);
      this.saveItems();
    }
  }

  addItems(itemIds: string[]): void {
    const newItems = itemIds.filter(id => !this.items.includes(id));
    if (newItems.length > 0) {
      this.items.push(...newItems);
      this.saveItems();
    }
  }

  removeItem(itemId: string): void {
    const index = this.items.indexOf(itemId);
    if (index > -1) {
      this.items.splice(index, 1);
      this.saveItems();
    }
  }

  clearItems(): void {
    this.items = [];
    this.saveItems();
  }

  hasItem(itemId: string): boolean {
    return this.items.includes(itemId);
  }

  /**
   * "Add all" reached the parent study, so every unit of this temporary study is on it — including the
   * ones a paginated client never loaded and cannot enumerate.
   *
   * One overwritten id rather than a set: only one temporary study is ever the current one, since every
   * creation path wipes the item list above (`working-conditions.component.ts:137,199`,
   * `model-search-working-conditions-sidebar.component.ts:209,293`). So there is nothing to cap and
   * nothing to prune. The id is stored rather than a bare boolean because a boolean has nothing to
   * disambiguate it: one "add all" would mark every later temporary study as fully added.
   */
  markAllAdded(temporaryStudyId: string): void {
    localStorage.setItem(ALL_ADDED_KEY, temporaryStudyId);
  }

  /**
   * Read live, not through the constructor cache `items` uses: the clearing sites above write
   * `localStorage` directly, and this is a click path rather than a hot one.
   */
  hasAllAdded(temporaryStudyId: string): boolean {
    return !!temporaryStudyId && localStorage.getItem(ALL_ADDED_KEY) === temporaryStudyId;
  }
}
